export type GetFocusabilityReturnValue = {
  focusable: boolean;
  visible: boolean;
};

/** Determines whether an element is visible to the DOM or not. */
export const isVisible = (element: HTMLElement) => {
  const style = window.getComputedStyle(element);

  return style.display !== "none" && style.visibility !== "hidden";
};

/** Determines whether an element is visible to the DOM and focusable or not. */
export const getFocusability = (element: HTMLElement): GetFocusabilityReturnValue => {
  if (isVisible(element)) {
    let focusable: boolean;

    if (element.tabIndex < 0) {
      focusable = false;
    } else if (element.inert) {
      focusable = false;
    } else {
      if ((element as HTMLInputElement).disabled) {
        focusable = false;
      } else {
        if (element.tabIndex >= 0) {
          focusable = true;
        } else {
          switch (element.nodeName) {
            case "A":
              const link = element as HTMLLinkElement;

              focusable = !!link.href && link.rel !== "ignore";
            case "INPUT":
              focusable = (element as HTMLInputElement).type !== "hidden";
            case "BUTTON":
            case "SELECT":
            case "TEXTAREA":
              focusable = true;
              break;
            default:
              focusable = false;
          }
        }
      }
    }

    return {
      focusable,
      visible: true
    };
  } else {
    return {
      focusable: false,
      visible: false
    };
  }
};

export const getFirstFocusableDescendant = (
  node: HTMLElement
): HTMLElement | undefined => {
  for (let i = 0; i < node.children.length; ++i) {
    const childElement = node.children[i] as HTMLElement;

    const focusability = getFocusability(childElement);

    if (focusability.visible) {
      if (focusability.focusable) {
        return childElement;
      } else {
        const first = getFirstFocusableDescendant(childElement);

        if (first) {
          return first;
        }
      }
    }
  }
};

export const getLastFocusableDescendant = (
  node: HTMLElement
): HTMLElement | undefined => {
  for (let i = node.children.length - 1; i >= 0; --i) {
    const childElement = node.children[i] as HTMLElement;

    const focusability = getFocusability(childElement);

    if (focusability.visible) {
      const last = getLastFocusableDescendant(childElement);

      if (last) {
        return last;
      } else {
        if (focusability.focusable) {
          return childElement;
        }
      }
    }
  }
};

export const focusFirstDescendant = (node: HTMLElement) => {
  const child = getFirstFocusableDescendant(node);

  if (child) {
    child.focus();

    return true;
  }
};

export const focusLastDescendant = (node: HTMLElement) => {
  const child = getLastFocusableDescendant(node);

  if (child) {
    child.focus();

    return true;
  }
};

/** Attempts to focus the next focusable element in DOM order.
 * Returns true if an element was focused. */
export const focusNextElement = (element: HTMLElement) => {
  if (focusFirstDescendant(element)) {
    return true;
  } else {
    let nextElement = element.nextElementSibling as HTMLElement | null;

    // Check siblings
    while (nextElement) {
      const focusability = getFocusability(nextElement);

      if (focusability.focusable) {
        nextElement.focus();

        return true;
      } else {
        if (focusFirstDescendant(nextElement)) {
          return true;
        }
      }

      nextElement = nextElement.nextElementSibling as HTMLElement | null;
    }

    // Get closest parent sibling
    let parent = element.parentElement;

    while (parent && nextElement === null) {
      nextElement = parent.nextElementSibling as HTMLElement | null;

      parent = parent.parentElement;
    }

    // Check closest parent sibling
    if (nextElement) {
      const focusability = getFocusability(nextElement);

      if (focusability.focusable) {
        nextElement.focus();

        return true;
      } else {
        if (focusNextElement(nextElement)) {
          return true;
        }
      }
    }
  }
};

export const isScrollable = (element: HTMLElement) => {
  const styles = window.getComputedStyle(element);
  const overflow = styles.overflowY;

  return (
    element.scrollHeight > element.clientHeight &&
    (overflow === "scroll" || overflow === "auto")
  );
};

export const elementIsScrolling = (element: HTMLElement, scrollingDown: boolean) => {
  let atTop = element.scrollTop === 0;

  let atBottom = element.scrollTop >= element.scrollHeight - element.clientHeight - 1;

  return (scrollingDown && !atBottom) || (!scrollingDown && !atTop);
};

/**
 * Traps scrolling by mousewheel and touch events to the element parameter.
 * Use the enabled parameter to dynamically enable/disable this behaviour.
 * @param element
 * @param enabled
 */
export const addScrollTrap = (element: HTMLElement, enabled?: () => boolean) => {
  let touchStart = 0;
  let touchDirectionDown: boolean | undefined;

  element.addEventListener("touchstart", e => {
    if (enabled?.()) {
      touchStart = e.touches[0].clientY;
      touchDirectionDown = undefined;
    }
  });

  element.addEventListener(
    "wheel",
    e => {
      if (enabled?.()) {
        if (e.target) {
          let target = e.target as HTMLElement | null;
          const isDown = e.deltaY > 0;

          while (target !== null && target !== element) {
            if (isScrollable(target) && elementIsScrolling(target, isDown)) {
              return;
            }

            target = target.parentElement;
          }

          e.preventDefault();
        }
      }
    },
    {
      passive: false
    }
  );

  element.addEventListener(
    "touchmove",
    e => {
      if (enabled?.()) {
        if (e.target) {
          let target = e.target as HTMLElement | null;

          if (touchDirectionDown === undefined) {
            touchDirectionDown = e.touches[0].clientY < touchStart;
          }

          while (target !== null && target !== element) {
            if (isScrollable(target) && elementIsScrolling(target, touchDirectionDown)) {
              return;
            }

            target = target.parentElement;
          }

          e.preventDefault();
        }
      }
    },
    {
      passive: false
    }
  );
};
